import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import type { EloTrail, TrailProgress } from './TrailCard';

interface TrailListItemProps {
  trail: EloTrail;
  progress?: TrailProgress;
  onPress: () => void;
}

const BookmarkIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.8192 4.39092C12.8192 2.68238 11.6524 2 9.97237 2H6.0279C4.3998 2 3.17969 2.63762 3.17969 4.27676V13.411C3.17904 13.736 3.44239 14 3.76802 14C3.8692 14 3.96845 13.9741 4.05666 13.9247L8.01864 11.7038L11.9436 13.9209C12.227 14.0804 12.5864 13.9805 12.746 13.6971C12.7959 13.6089 12.8225 13.5096 12.8225 13.4078L12.8192 4.39092Z"
      stroke="#7E7D75"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ChevronIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path d="M10 8L14 12L10 16" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
  </Svg>
);

const MARKER_PATH =
  "M27.4531 44.3675L22.9759 22.4527L38.2176 19.5L36.2706 25.8987L40.4486 30.9797L25.4101 34.154M21 44.5H43";

const TILE_COUNT = 6;
const TILE_INDICES = Array.from({ length: TILE_COUNT }, (_, i) => i);

const TiledMarkerBackground = () => (
  <View style={styles.tiledGrid}>
    {TILE_INDICES.map((i) => (
      <Svg key={i} width={22} height={25} viewBox="21 19.5 22 25" fill="none" style={{ opacity: 0.5 }}>
        <Path d={MARKER_PATH} fill="#C0D1DB" />
        <Path d={MARKER_PATH} stroke="#C0D1DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </Svg>
    ))}
  </View>
);

const GemIconWhite = () => (
  <Svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <Path
      d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M1.50391 5.10156H10.4954" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const PlusThumbnailOverlay = () => (
  <View style={styles.plusOverlay} pointerEvents="none">
    {/* 46×46 group pinned to top-left corner */}
    <View style={styles.plusGroup}>
      {/* 8×8 anti-alias piece — right of the main block */}
      <View style={styles.plusCornerSvgRight}>
        <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <Path d="M0 0H8C1.29032 0 0 2.83871 0 8V0Z" fill="#669AB7" />
        </Svg>
      </View>
      {/* 8×8 anti-alias piece — below the main block */}
      <View style={styles.plusCornerSvgBottom}>
        <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <Path d="M0 0H8C1.29032 0 0 2.83871 0 8V0Z" fill="#669AB7" />
        </Svg>
      </View>
      {/* 24×24 main blue block pinned to top-left of the group */}
      <View style={styles.plusMainBlock}>
        <GemIconWhite />
      </View>
    </View>
  </View>
);

export default function TrailListItem({ trail, progress, onPress }: TrailListItemProps) {
  const { t } = useLanguage();
  const stepCount = trail.step_count;
  const pct = progress?.progressPercent ?? 0;
  const hasProgress = progress?.hasStarted ?? false;
  const isPlus = !!trail.is_plus;

  return (
    <TouchableOpacity style={styles.container} onPress={onPress} activeOpacity={0.7}>
      <View style={[styles.thumbnail, isPlus && styles.thumbnailPlus]}>
        {trail.cover_image_url ? (
          <Image
            source={{ uri: trail.cover_image_url }}
            style={StyleSheet.absoluteFill}
            contentFit="cover"
            transition={150}
            cachePolicy="memory-disk"
          />
        ) : (
          <TiledMarkerBackground />
        )}
        {isPlus && <PlusThumbnailOverlay />}
      </View>

      <View style={styles.info}>
        <Text style={styles.title} numberOfLines={2}>{trail.name}</Text>

        {hasProgress && (
          <View style={styles.progressRow}>
            <Text style={styles.progressPct}>{pct}%</Text>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { width: `${pct}%` as any }]} />
            </View>
          </View>
        )}

        <View style={styles.metaRow}>
          <BookmarkIcon />
          <Text style={styles.metaText}>
            {stepCount} {t('trail.home.steps')}
          </Text>
        </View>
      </View>

      <ChevronIcon />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  thumbnail: {
    width: 64,
    height: 64,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#E6EBF0',
    flexShrink: 0,
  },
  thumbnailPlus: {
    borderColor: '#669AB7',
    borderWidth: 1,
  },
  tiledGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    padding: 7,
    paddingHorizontal: 6,
  },
  plusOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  /* 46×46 group pinned to top-left corner; rounded inward at bottom-right */
  plusGroup: {
    width: 46,
    height: 46,
    borderBottomRightRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  /* 8×8 anti-alias piece — just to the right of the main block */
  plusCornerSvgRight: {
    position: 'absolute',
    top: 0,
    left: 24,
  },
  /* 8×8 anti-alias piece — just below the main block */
  plusCornerSvgBottom: {
    position: 'absolute',
    top: 24,
    left: 0,
  },
  /* 24×24 main blue block pinned to top-left corner of group */
  plusMainBlock: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 24,
    height: 24,
    borderBottomRightRadius: 12,
    backgroundColor: '#669AB7',
    justifyContent: 'center',
    alignItems: 'center',
  },
  info: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontSize: 16,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 22.4,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  progressPct: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#669AB7',
    lineHeight: 16.8,
  },
  progressBar: {
    flex: 1,
    height: 2,
    backgroundColor: '#F0EEE4',
    borderRadius: 2,
  },
  progressFill: {
    height: 2,
    backgroundColor: '#669AB7',
    borderRadius: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    lineHeight: 12,
  },
});
